import Phaser from 'phaser';
import {
  loadSave,
  vibraniumCount,
  VIBRANIUM_TIERS,
} from './save';
import { INFINITY_STONE_ORDER, STONES } from './stones';

export class PowerUpsScene extends Phaser.Scene {
  constructor() {
    super('PowerUps');
  }

  create() {
    this.cameras.main.setBackgroundColor('#0a0f1f');
    this.layout();
    this.scale.on('resize', () => {
      this.children.removeAll(true);
      this.layout();
    });
  }

  private layout() {
    const w = this.scale.width;
    const h = this.scale.height;

    this.add
      .text(w / 2, h * 0.08, 'Power-Ups', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '34px',
        color: '#ffd24a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.drawBackButton();
    this.drawVibraniumSection(w, h);
    this.drawStonesSection(w, h);
  }

  private drawBackButton() {
    const g = this.add.graphics();
    const cx = 32;
    const cy = 32;
    g.fillStyle(0x121a32, 1);
    g.lineStyle(2, 0x0a0f1f, 1);
    g.fillCircle(cx, cy, 22);
    g.strokeCircle(cx, cy, 22);
    g.lineStyle(3, 0xffffff, 1);
    g.beginPath();
    g.moveTo(cx + 6, cy - 8);
    g.lineTo(cx - 6, cy);
    g.lineTo(cx + 6, cy + 8);
    g.strokePath();
    const hit = this.add.zone(cx, cy, 56, 56).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => this.scene.start('LevelMap'));
  }

  private drawVibraniumSection(w: number, h: number) {
    const save = loadSave();
    const count = vibraniumCount(save);
    const topY = Math.floor(h * 0.2);

    this.add
      .text(w / 2, topY, `Vibranium: ${count}`, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '22px',
        color: '#e0e7ff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const tierStartY = topY + 40;
    const lineH = Math.min(30, Math.floor((h * 0.4) / VIBRANIUM_TIERS.length));

    VIBRANIUM_TIERS.forEach((tier, i) => {
      const y = tierStartY + i * lineH;
      const unlocked = count >= tier.threshold;
      const color = unlocked ? '#86efac' : '#64748b';
      const prefix = unlocked ? '✓' : `${tier.threshold}`;
      this.add
        .text(
          Math.floor(w * 0.15),
          y,
          `${prefix}  ${tier.name} — ${tier.description}`,
          {
            fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
            fontSize: '16px',
            color,
            fontStyle: unlocked ? 'bold' : 'normal',
          },
        )
        .setOrigin(0, 0.5);
    });
  }

  private drawStonesSection(w: number, h: number) {
    const save = loadSave();
    const earned = new Set(save.stonesEarned);
    const sectionY = Math.floor(h * 0.72);

    this.add
      .text(w / 2, sectionY - 36, 'Infinity Stones', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '24px',
        color: '#ffd24a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const count = INFINITY_STONE_ORDER.length;
    const spacing = Math.min(72, Math.floor((w * 0.9) / count));
    const totalW = count * spacing;
    const startX = Math.floor((w - totalW) / 2 + spacing / 2);

    INFINITY_STONE_ORDER.forEach((id, i) => {
      const cx = startX + i * spacing;
      const cy = sectionY;
      const spec = STONES[id];
      const has = earned.has(id);

      const gem = this.add.graphics();
      gem.fillStyle(has ? spec.color : 0x1f2937, 1);
      gem.lineStyle(3, has ? 0x0a0f1f : 0x374151, 1);
      gem.fillCircle(cx, cy, 22);
      gem.strokeCircle(cx, cy, 22);
      if (has) {
        gem.fillStyle(0xffffff, 0.3);
        gem.fillCircle(cx - 6, cy - 6, 6);
      }

      this.add
        .text(cx, cy + 42, spec.name.split(' ')[0], {
          fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
          fontSize: '12px',
          color: has ? '#f4f4f4' : '#64748b',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);

      if (has) {
        this.add
          .text(cx, cy + 58, spec.tagline, {
            fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
            fontSize: '10px',
            color: '#a3a3a3',
            wordWrap: { width: spacing - 6 },
            align: 'center',
          })
          .setOrigin(0.5, 0);
      }
    });
  }
}
